import React, { useEffect, useState, useCallback } from "react";
import "./LeadDetails.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../config/env";

interface TimelineItem {
  id: string;
  type: string;
  agent_name: string | null;
  role_id?: number;
  timeline_time: string;
  otherDetails: {
    source_of_lead?: string;
    client_name: string;
    phone?: string;
    generated_for_project?: string;
    activity?: string;
    remark?: string;
    despositions_name?: string;
    recording_link?: string;
    call_duration?: string;
    meeting_date?: string;
    meeting_time?: string;
    address?: string;
    image?: string;
    developer_name?: string;
    cluster_name?: string;
    project_name?: string;
    date?: string;
    time?: string;
    property_type?: string;
    budget_min?: number;
    budget_max?: number;
    budget_type?: string;
    budget?: string;
    possession_expected?: string;
    requirement?: string;
    f2f_image?: string;
    [key: string]: any;
  };
  profile_image: string | null;
  time_diff: string;
}

interface LeadData {
  hasPermission: number;
  timeline: TimelineItem[];
  user: {
    name: string;
    role: {
      display_name: string;
    };
  };
  closedLeadDetails: any;
  getLeadRatingData: any;
}

// Interface for Closed Lead Details
interface ClosedLeadDetails {
  type: string;
  lead_id: string;
  project: {
    id: number;
    name: string;
    address: string;
  };
  user: {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    mobile_number: string;
  };
  flat_no: string;
  config: string;
  agreement_val: string;
  cheque_no: string;
  selfie: string;
  property_type: string;
  client_name: string;
  client_number: string;
  developer_name: string;
  project_name: string;
  cluster_tower: string;
  configuration: string;
  consideration_value: string;
  agreement_value: string;
  total_cost: string;
  developer_rm: string;
  developer_rm_name: string;
  developer_rm_contact: string;
  remarks: string;
  welcome_call: string;
  welcome_call_mail: string;
  welcome_call_mail_remark: string;
  booking_form: string;
  builder_confirmation: string;
  cheque_payment: string;
  cost_sheet: string;
  adhaar_card: string;
  pan_card: string;
  created_at: string;
  refer_to?: string;
  referral_raised_status?: string;
  referral_amount?: string;
  manager_remark?: string;
  supervisor_remark?: string;
}

const LeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"updated" | "initial">("updated");
  const location = useLocation();

  // Get raw lead details from location state (sent from TableView)
  const locationState = location.state || {};
  
  const rawLeadDetails = {
    raw_lead_id: locationState.raw_lead_id || null,
    raw_leads_remarks: locationState.raw_leads_remarks || "",
    customer_address: locationState.customer_address || "",
    raw_leads_buying_location: locationState.raw_leads_buying_location || "",
    raw_leads_property_type: locationState.raw_leads_property_type || "",
    raw_leads_configuration: locationState.raw_leads_configuration || "",
    raw_leads_budget_max: locationState.raw_leads_budget_max || "",
    raw_leads_budget_min: locationState.raw_leads_budget_min || "",
    voice_note: locationState.voice_note || "",
    name: locationState.name || "",
    email: locationState.email || "",
    is_valid: locationState.is_valid !== undefined ? locationState.is_valid : 1,
  };

  console.log("Raw Lead Details from location state:", rawLeadDetails);

  const getRoleDisplayName = useCallback((roleId?: number): string => {
    switch (roleId) {
      case 4:
        return "Sales Agent";
      case 5:
        return "Presales Agent";
      default:
        return "Sales Agent";
    }
  }, []);

  const formatTimelineItem = useCallback(
    (item: TimelineItem) => {
      const { type, timeline_time, otherDetails, time_diff, role_id } = item;
      const roleDisplayName = getRoleDisplayName(role_id);

      switch (type) {
        case "leadgen":
          return {
            icon: "📥",
            title: "Lead Generated",
            description: `Source: ${otherDetails.source_of_lead || "Unknown"}`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "system_call":
          return {
            icon: "📞",
            title: "Call Attempted",
            description: "System call was made to client",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "sales_recording_call":
          return {
            icon: "🎙️",
            title: "Sales Recording",
            description: "Sales call recording available",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
          };
        case "lead":
          return {
            icon: "📋",
            title: "Lead Details Updated",
            description:
              otherDetails.requirement || "Lead requirements updated",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "meeting":
          return {
            icon: "📅",
            title: "Meeting Scheduled",
            description: `Meeting at ${
              otherDetails.address || "Unknown location"
            } on ${otherDetails.meeting_date || ""} ${
              otherDetails.meeting_time || ""
            }`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "recording":
          return {
            icon: "🎵",
            title: "Recording Added",
            description: `Call duration: ${
              otherDetails.call_duration || "N/A"
            } seconds`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
          };
        case "disposition":
          const isF2FDone = otherDetails.despositions_name === "F2F Done";
          const isSiteVisitDone = otherDetails.despositions_name === "Site Visit Done";
          const isTokenDone = otherDetails.despositions_name === "Token Done";
          const hasImage = otherDetails.f2f_image || otherDetails.image;
          
          return {
            icon: isF2FDone ? "✅" : isTokenDone ? "🪙" : isSiteVisitDone ? "📍" : "🏷️",
            title: otherDetails.despositions_name || "Disposition Updated",
            description: otherDetails.remark || "Status updated",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
            isF2FDone: isF2FDone,
            isTokenDone: isTokenDone,
            isSiteVisitDone: isSiteVisitDone,
            hasImage: !!hasImage,
            imageUrl: hasImage || null,
            otherDetails: otherDetails,
          };
        case "closed_lead":
          return {
            icon: "🔒",
            title: "Lead Closed",
            description: "Lead has been closed successfully",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            isClosed: true,
            otherDetails: otherDetails,
          };
        default:
          return {
            icon: "🔹",
            title: type,
            description: "Activity performed",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
      }
    },
    [getRoleDisplayName]
  );

  const handleImageClick = useCallback((imageUrl: string | undefined) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
    }
  }, []);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Session expired, please login again");
          navigate("/");
          return;
        }

        // Use the actual lead ID from URL params
        const leadId = id || rawLeadDetails.raw_lead_id;
        if (!leadId) {
          console.error("No lead ID found");
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${API_BASE_URL}/api/leads/timeline/${leadId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        console.log("Lead Details Response:", res.data);
        setLeadData(res.data);
      } catch (error: any) {
        console.error("Error fetching lead details:", error);

        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          alert("Session expired, please login again");
          navigate("/");
        } else {
          alert("Cannot fetch lead details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetails();
  }, [id, navigate, rawLeadDetails.raw_lead_id]);

  if (loading) return <p className="loadingText">Loading lead details...</p>;
  if (!leadData) return <p className="loadingText">No lead data found</p>;

  // Sort timeline: closed_lead should be at the end
  const sortedTimeline = [...leadData.timeline].sort((a, b) => {
    if (a.type === "closed_lead" && b.type !== "closed_lead") return 1;
    if (b.type === "closed_lead" && a.type !== "closed_lead") return -1;
    return new Date(a.timeline_time).getTime() - new Date(b.timeline_time).getTime();
  });

  // Closed lead is rendered once as the final timeline entry using closedLeadDetails.
  // Exclude the lightweight closed_lead event here to avoid duplicate closed entries.
  const filteredTimeline = sortedTimeline.filter(
    (item) => item.type !== "activity" && item.type !== "closed_lead"
  );

  const firstTimelineItem = filteredTimeline[0];
  const clientName =
    firstTimelineItem?.otherDetails?.client_name ||
    leadData.user?.name ||
    rawLeadDetails.name ||
    "Client";

  const closedLeadDetails: ClosedLeadDetails | null = leadData.closedLeadDetails || null;

  const latestDisposition = [...filteredTimeline]
    .reverse()
    .find((item) => item.type === "disposition");
  const currentStatus = closedLeadDetails
    ? "Closed"
    : latestDisposition?.otherDetails?.despositions_name || "New Lead";

  const propertyRequirements = filteredTimeline.filter(
    (item) => item.type === "lead"
  );

  const latestLeadDetails =
    propertyRequirements[propertyRequirements.length - 1]?.otherDetails || {};
  const initialLeadDetails = propertyRequirements[0]?.otherDetails || {};
  
  const realtimeInitialDetails = {
    lead_id: initialLeadDetails.lead_id || id || "N/A",
    city: initialLeadDetails.city || "N/A",
    buying_location: initialLeadDetails.buying_location || rawLeadDetails.raw_leads_buying_location || "N/A",
    budget_range:
      initialLeadDetails.budget_min && initialLeadDetails.budget_max
        ? `₹${initialLeadDetails.budget_min} Lakhs - ₹${initialLeadDetails.budget_max} Lakhs`
        : initialLeadDetails.budget || "N/A",
    bhk: initialLeadDetails.bhk || initialLeadDetails.configuration || rawLeadDetails.raw_leads_configuration || "N/A",
    property_type: initialLeadDetails.property_type || rawLeadDetails.raw_leads_property_type || "N/A",
    customer_address: initialLeadDetails.customer_address || rawLeadDetails.customer_address || "N/A",
  };

  return (
    <div className="leadPage">
      {selectedImage && (
        <div className="imageModal" onClick={() => setSelectedImage(null)}>
          <div className="imageModalContent">
            <span className="closeModal" onClick={() => setSelectedImage(null)}>
              &times;
            </span>
            <img src={selectedImage} alt="Site Visit" className="modalImage" />
          </div>
        </div>
      )}

      <div className="leadDetailsContainer">
        <div className="leadHeader">
          <div className="headerTop" onClick={() => navigate("/dashboard")}>
            ← Back to Leads
          </div>

          <div className="leadProfile">
            <div className="avatar">👤</div>
            <div className="profileInfo">
              <h2>{clientName}</h2>
              <button
                className={`followBtn ${currentStatus
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {currentStatus}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="contentContainer">
        <div className="tabsContainer">
          <button
            className={`tabBtn ${activeTab === "updated" ? "active" : ""}`}
            onClick={() => setActiveTab("updated")}
          >
            Updated Details
          </button>
          <button
            className={`tabBtn ${activeTab === "initial" ? "active" : ""}`}
            onClick={() => setActiveTab("initial")}
          >
            Initial Details
          </button>
        </div>

        {activeTab === "updated" && (
          <div className="cardBox">
            <h3>Client Updated Details</h3>
            {propertyRequirements.map((leadItem, index) => (
              <div key={index} className="detailsTable">
                <div className="tableRow">
                  <div className="tableLabel">LEAD ID</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.lead_id}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">Project</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.project_name || "N/A"}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BUYING LOCATION</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.buying_location}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BUDGET RANGE</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.budget_min &&
                    leadItem.otherDetails.budget_max
                      ? `₹${leadItem.otherDetails.budget_min} - ₹${
                          leadItem.otherDetails.budget_max
                        } ${leadItem.otherDetails.budget_type || "Lakhs"}`
                      : leadItem.otherDetails.budget || "N/A"}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BHK</div>
                  <div className="tableValue">{realtimeInitialDetails.bhk}</div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">PROPERTY TYPE</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.property_type}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">CUSTOMER ADDRESS</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.customer_address}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">REMARKS</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.requirement || leadItem.otherDetails.remarks || rawLeadDetails.raw_leads_remarks || "N/A"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "initial" && (
          <div className="cardBox">
            <h3>Client Initial Details</h3>
            <div className="detailsTable">
              <div className="tableRow">
                <div className="tableLabel">LEAD ID</div>
                <div className="tableValue">{rawLeadDetails.raw_lead_id || "N/A"}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUYING LOCATION</div>
                <div className="tableValue">{rawLeadDetails.raw_leads_buying_location || "N/A"}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUDGET RANGE</div>
                <div className="tableValue">
                  {rawLeadDetails.raw_leads_budget_min && rawLeadDetails.raw_leads_budget_max 
                    ? `₹ ${rawLeadDetails.raw_leads_budget_min} - ₹ ${rawLeadDetails.raw_leads_budget_max} Lakhs`
                    : rawLeadDetails.raw_leads_budget_min 
                      ? `₹ ${rawLeadDetails.raw_leads_budget_min} Lakhs`
                      : rawLeadDetails.raw_leads_budget_max 
                        ? `Up to ₹ ${rawLeadDetails.raw_leads_budget_max} Lakhs`
                        : "N/A"}
                </div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BHK</div>
                <div className="tableValue">{rawLeadDetails.raw_leads_configuration || "N/A"}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">PROPERTY TYPE</div>
                <div className="tableValue">{rawLeadDetails.raw_leads_property_type || "N/A"}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CUSTOMER ADDRESS</div>
                <div className="tableValue">{rawLeadDetails.customer_address || "N/A"}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">REMARKS</div>
                <div className="tableValue">{rawLeadDetails.raw_leads_remarks || "N/A"}</div>
              </div>
            </div>
          </div>
        )}

        <div className="cardBox">
          <h3>Timeline</h3>
          <div className="timelineContainer">
            {filteredTimeline.map((item, index) => {
              const formattedItem = formatTimelineItem(item);
              const isSiteVisit =
                item.type === "disposition" &&
                item.otherDetails.despositions_name === "Site Visit Done";
              const isF2FDone =
                item.type === "disposition" &&
                item.otherDetails.despositions_name === "F2F Done";
              const isTokenDone =
                item.type === "disposition" &&
                item.otherDetails.despositions_name === "Token Done";
              const isClosed = item.type === "closed_lead";
              const hasF2FImage = isF2FDone && (item.otherDetails.f2f_image || item.otherDetails.image);
              const hasRecordingLink =
                item.otherDetails.recording_link &&
                item.otherDetails.recording_link.trim() !== "";

              return (
                <div key={item.id} className="timelineRow">
                  {index !== 0 && <div className="timelineLine"></div>}

                  <div className="timelineIcon">{formattedItem.icon}</div>

                  <div className="timelineContent">
                    <div className="timelineHeader">
                      <h4 className="timelineTitle">{formattedItem.title}</h4>
                      <span className="timelineTime">
                        {new Date(formattedItem.time).toLocaleDateString()} •{" "}
                        {new Date(formattedItem.time).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>

                    <p className="timelineDescription">
                      {formattedItem.description}
                    </p>

                    <p className="timelineAgent">👤 {formattedItem.role}</p>

                    {isClosed && (
                      <div className="closed-timeline-card">
                        <div className="closed-timeline-header">
                          <span className="closed-timeline-icon">🔒</span>
                          <span className="closed-timeline-title">Lead Closed</span>
                        </div>
                        <div className="closed-timeline-details">
                          {item.otherDetails.remark && (
                            <div className="detail-row">
                              <span className="detail-label">Remarks:</span>
                              <span className="detail-value">{item.otherDetails.remark}</span>
                            </div>
                          )}
                          {item.otherDetails.closed_date && (
                            <div className="detail-row">
                              <span className="detail-label">Closed Date:</span>
                              <span className="detail-value">{item.otherDetails.closed_date}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {isTokenDone && (
                      <div className="token-card">
                        <div className="token-header">
                          <span className="token-icon">🪙</span>
                          <span className="token-title">Token Done</span>
                        </div>
                        <div className="token-details">
                          {item.otherDetails.remark && (
                            <div className="detail-row">
                              <span className="detail-label">Remarks:</span>
                              <span className="detail-value">{item.otherDetails.remark}</span>
                            </div>
                          )}
                          {item.otherDetails.token_amount && (
                            <div className="detail-row">
                              <span className="detail-label">Amount:</span>
                              <span className="detail-value highlight">₹ {item.otherDetails.token_amount}</span>
                            </div>
                          )}
                          {item.otherDetails.token_date && (
                            <div className="detail-row">
                              <span className="detail-label">Date:</span>
                              <span className="detail-value">{item.otherDetails.token_date}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {hasRecordingLink && !isSiteVisit && !isF2FDone && !isClosed && !isTokenDone && (
                      <div className="recordingCard">
                        <div className="recordingHeader">
                          <span className="recordingIcon">🎙️</span>
                          <span className="recordingTitle">Call Recording</span>
                        </div>
                        <audio controls className="audioPlayer">
                          <source
                            src={item.otherDetails.recording_link}
                            type="audio/mpeg"
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}

                    {isF2FDone && (
                      <div className="f2f-done-card">
                        <div className="f2f-done-details">
                          {item.otherDetails.meeting_date && (
                            <div className="detail-row">
                              <span className="detail-label">Date:</span>
                              <span className="detail-value">{item.otherDetails.meeting_date}</span>
                            </div>
                          )}
                          {item.otherDetails.meeting_time && (
                            <div className="detail-row">
                              <span className="detail-label">Time:</span>
                              <span className="detail-value">{item.otherDetails.meeting_time}</span>
                            </div>
                          )}
                          {item.otherDetails.address && (
                            <div className="detail-row">
                              <span className="detail-label">Address:</span>
                              <span className="detail-value">{item.otherDetails.address}</span>
                            </div>
                          )}
                        </div>

                        {hasF2FImage && (
                          <div className="f2f-image-container">
                            <div className="f2f-image-wrapper">
                              <img
                                src={item.otherDetails.f2f_image || item.otherDetails.image}
                                alt="F2F Meeting"
                                className="f2f-image"
                                onClick={() => handleImageClick(item.otherDetails.f2f_image || item.otherDetails.image)}
                              />
                              <div 
                                className="f2f-image-overlay"
                                onClick={() => handleImageClick(item.otherDetails.f2f_image || item.otherDetails.image)}
                              >
                                <span className="view-icon">🔍</span>
                                <span>Click to view</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {hasRecordingLink && (
                          <div className="recording-inline">
                            <div className="recording-header-inline">
                              <span className="recording-icon-inline">🎙️</span>
                              <span className="recording-title-inline">Call Recording</span>
                            </div>
                            <audio controls className="audio-player-inline">
                              <source
                                src={item.otherDetails.recording_link}
                                type="audio/mpeg"
                              />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        )}
                      </div>
                    )}

                    {isSiteVisit && (
                      <div className="siteVisitInlineCard">
                        <div className="siteVisitGrid">
                          <div className="infoSection">
                            <div className="detailRow">
                              <span className="detailLabel">Developer:</span>
                              <span className="detailValue">{item.otherDetails.developer_name || "N/A"}</span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Cluster:</span>
                              <span className="detailValue">{item.otherDetails.cluster_name || "N/A"}</span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Project Name:</span>
                              <span className="detailValue">{item.otherDetails.project_name || "N/A"}</span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Remarks:</span>
                              <span className="detailValue">{item.otherDetails.remark || "N/A"}</span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Disposition:</span>
                              <span className="detailValue highlight">{item.otherDetails.despositions_name || "N/A"}</span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Scheduled Date Time:</span>
                              <span className="detailValue">
                                {item.otherDetails.date && item.otherDetails.time
                                  ? `${item.otherDetails.date} ${item.otherDetails.time}`
                                  : item.otherDetails.meeting_date && item.otherDetails.meeting_time
                                  ? `${item.otherDetails.meeting_date} ${item.otherDetails.meeting_time}`
                                  : "N/A"}
                              </span>
                            </div>

                            {item.otherDetails.recording_link && (
                              <div className="recordingInline">
                                <div className="recordingHeaderInline">
                                  <span className="recordingIconInline">🎙️</span>
                                  <span className="recordingTitleInline">Call Recording</span>
                                </div>
                                <audio controls className="audioPlayerInline">
                                  <source src={item.otherDetails.recording_link} type="audio/mpeg" />
                                  Your browser does not support the audio element.
                                </audio>
                              </div>
                            )}
                          </div>

                          {item.otherDetails.image && (
                            <div className="imageSection">
                              <div className="imageWrapper">
                                <img
                                  src={item.otherDetails.image}
                                  alt="Site Visit"
                                  className="siteVisitImageInline"
                                  onClick={() => handleImageClick(item.otherDetails.image)}
                                />
                                <div
                                  className="imageOverlayInline"
                                  onClick={() => handleImageClick(item.otherDetails.image)}
                                >
                                  <span className="viewIconInline">🔍</span>
                                  <span>Click to view</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {closedLeadDetails && (
              <div className="timelineRow closed-lead-final-row">
                {filteredTimeline.length > 0 && <div className="timelineLine"></div>}

                <div className="timelineIcon">🔒</div>

                <div className="timelineContent">
                  <div className="timelineHeader">
                    <h4 className="timelineTitle">Closed Lead Transaction Details</h4>
                    <span className="timelineTime">
                      {closedLeadDetails.created_at
                        ? `${new Date(closedLeadDetails.created_at).toLocaleDateString()} • ${new Date(
                            closedLeadDetails.created_at
                          ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                        : ""}
                    </span>
                  </div>

                  <p className="timelineDescription">
                    Final transaction details recorded for this closed lead.
                  </p>

                  <div className="closed-timeline-card closed-transaction-card">
                    <div className="closed-section">
                      <h4>🏠 Property Details</h4>
                      <div className="detailsTable">
                        <div className="tableRow">
                          <div className="tableLabel">Project</div>
                          <div className="tableValue">{closedLeadDetails.project_name || closedLeadDetails.project?.name || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Flat No.</div>
                          <div className="tableValue">{closedLeadDetails.flat_no || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Config</div>
                          <div className="tableValue">{closedLeadDetails.config || closedLeadDetails.configuration || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Property Type</div>
                          <div className="tableValue">{closedLeadDetails.property_type || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Cluster / Tower</div>
                          <div className="tableValue">{closedLeadDetails.cluster_tower || "N/A"}</div>
                        </div>
                      </div>
                    </div>

                    <div className="closed-section">
                      <h4>💳 Payment Details</h4>
                      <div className="detailsTable">
                        <div className="tableRow">
                          <div className="tableLabel">Agreement Value</div>
                          <div className="tableValue">₹ {closedLeadDetails.agreement_value || closedLeadDetails.agreement_val || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Consideration Value</div>
                          <div className="tableValue">₹ {closedLeadDetails.consideration_value || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Total Cost</div>
                          <div className="tableValue">₹ {closedLeadDetails.total_cost || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Cheque No</div>
                          <div className="tableValue">{closedLeadDetails.cheque_no || "N/A"}</div>
                        </div>
                      </div>
                    </div>

                    <div className="closed-section">
                      <h4>👤 Lead Details</h4>
                      <div className="detailsTable">
                        <div className="tableRow">
                          <div className="tableLabel">Client Name</div>
                          <div className="tableValue">{closedLeadDetails.client_name || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Client Number</div>
                          <div className="tableValue">{closedLeadDetails.client_number || "N/A"}</div>
                        </div>
                      </div>
                    </div>

                    {/* <div className="closed-section">
                      <h4>🏗️ Developer Details</h4>
                      <div className="detailsTable">
                        <div className="tableRow">
                          <div className="tableLabel">Developer Name</div>
                          <div className="tableValue">{closedLeadDetails.developer_name || "N/A"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Developer RM</div>
                          <div className="tableValue">{closedLeadDetails.developer_rm || closedLeadDetails.developer_rm_name || "N/A"}</div>
                        </div>
                        {closedLeadDetails.developer_rm_contact && (
                          <div className="tableRow">
                            <div className="tableLabel">Developer RM Contact</div>
                            <div className="tableValue">{closedLeadDetails.developer_rm_contact}</div>
                          </div>
                        )}
                      </div>
                    </div> */}

                    {closedLeadDetails.selfie && (
                      <div className="closed-section">
                        <h4>📸 Selfie</h4>
                        <div className="imageWrapper">
                          <img
                            src={closedLeadDetails.selfie}
                            alt="Selfie"
                            className="closed-image"
                            onClick={() => handleImageClick(closedLeadDetails.selfie)}
                          />
                        </div>
                      </div>
                    )}

                    <div className="closed-section">
                      <h4>📝 Remarks</h4>
                      <div className="remarks-box">
                        <p>{closedLeadDetails.remarks || "N/A"}</p>
                      </div>
                    </div>

                    <div className="closed-section">
                      <h4>📞 Welcome Call</h4>
                      <div className="detailsTable">
                        <div className="tableRow">
                          <div className="tableLabel">Welcome Call</div>
                          <div className="tableValue">{closedLeadDetails.welcome_call || "No"}</div>
                        </div>
                        <div className="tableRow">
                          <div className="tableLabel">Welcome Call Mail</div>
                          <div className="tableValue">{closedLeadDetails.welcome_call_mail || "No"}</div>
                        </div>
                        {closedLeadDetails.welcome_call_mail_remark && (
                          <div className="tableRow">
                            <div className="tableLabel">Welcome Call Remark</div>
                            <div className="tableValue">{closedLeadDetails.welcome_call_mail_remark}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {(closedLeadDetails.booking_form ||
                      closedLeadDetails.builder_confirmation ||
                      closedLeadDetails.cheque_payment ||
                      closedLeadDetails.cost_sheet ||
                      closedLeadDetails.adhaar_card ||
                      closedLeadDetails.pan_card) && (
                      <div className="closed-section">
                        <h4>📄 Documents</h4>
                        <div className="document-grid">
                          {[
                            ["Booking Form", closedLeadDetails.booking_form],
                            ["Builder Confirmation", closedLeadDetails.builder_confirmation],
                            ["Cheque Payment", closedLeadDetails.cheque_payment],
                            ["Cost Sheet", closedLeadDetails.cost_sheet],
                            ["Aadhaar Card", closedLeadDetails.adhaar_card],
                            ["PAN Card", closedLeadDetails.pan_card],
                          ].map(([label, imageUrl]) =>
                            imageUrl ? (
                              <div className="document-item" key={label as string}>
                                <p>{label}</p>
                                <img
                                  src={imageUrl as string}
                                  alt={label as string}
                                  className="document-thumbnail"
                                  onClick={() => handleImageClick(imageUrl as string)}
                                />
                              </div>
                            ) : null
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;